/**
 * @fileoverview Vue composable for device detection and mobile optimization
 * Provides reactive device info and performance optimizations
 */

import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAppStore } from '@/stores/app';

/**
 * Device detection and mobile optimization composable
 * @returns {Object} Device info and optimization methods
 */
export function useDevice() {
  const store = useAppStore();

  const width = ref(typeof window !== 'undefined' ? window.innerWidth : 0);
  const height = ref(typeof window !== 'undefined' ? window.innerHeight : 0);
  const dpr = ref(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);

  // Computed device properties
  const isMobile = computed(() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    return width.value < 768 || /mobile|android|iphone/i.test(ua);
  });

  const isTablet = computed(() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    return !isMobile.value && (width.value < 1024 || /ipad|tablet/i.test(ua));
  });

  const isDesktop = computed(() => !isMobile.value && !isTablet.value);

  const isTouch = computed(() => {
    if (typeof window === 'undefined') return false;
    return 'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0;
  });

  const os = computed(() => {
    if (typeof navigator === 'undefined') return 'unknown';
    const ua = navigator.userAgent || '';
    if (/android/i.test(ua)) return 'android';
    if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
    if (/mac/i.test(ua)) return 'macos';
    if (/win/i.test(ua)) return 'windows';
    if (/linux/i.test(ua)) return 'linux';
    return 'unknown';
  });

  const isLowEnd = computed(() => {
    if (typeof navigator === 'undefined') return false;
    const cores = navigator.hardwareConcurrency || 4;
    const memory = navigator.deviceMemory || 4; // GB
    return cores < 4 || memory < 4;
  });

  // Device info object
  const deviceInfo = computed(() => ({
    isMobile: isMobile.value,
    isTablet: isTablet.value,
    isDesktop: isDesktop.value,
    isTouch: isTouch.value,
    width: width.value,
    height: height.value,
    dpr: dpr.value,
    os: os.value,
    isLowEnd: isLowEnd.value
  }));

  /**
   * Get optimized globe configuration for current device
   * @returns {Object} Globe configuration
   */
  function getGlobeConfig() {
    const baseConfig = {
      particleCount: 1000,
      animationQuality: 'high',
      enableGlow: true,
      enableReflection: true,
      maxFlows: 50,
      rotationSpeed: 0.001
    };

    if (isMobile.value) {
      return {
        ...baseConfig,
        particleCount: isLowEnd.value ? 200 : 400,
        animationQuality: isLowEnd.value ? 'low' : 'medium',
        enableGlow: !isLowEnd.value,
        enableReflection: false,
        maxFlows: isLowEnd.value ? 15 : 25,
        rotationSpeed: 0.0005
      };
    }

    if (isTablet.value) {
      return {
        ...baseConfig,
        particleCount: 600,
        animationQuality: 'medium',
        enableGlow: true,
        enableReflection: false,
        maxFlows: 35
      };
    }

    if (isLowEnd.value) {
      return {
        ...baseConfig,
        particleCount: 600,
        animationQuality: 'medium',
        enableGlow: true,
        enableReflection: false,
        maxFlows: 30
      };
    }

    return baseConfig;
  }

  /**
   * Apply performance optimizations based on device
   */
  function applyPerformanceOptimizations() {
    if (typeof document === 'undefined') return;

    const html = document.documentElement;

    // Add device-specific classes
    if (isMobile.value) html.classList.add('is-mobile');
    if (isTablet.value) html.classList.add('is-tablet');
    if (isDesktop.value) html.classList.add('is-desktop');
    if (isTouch.value) html.classList.add('has-touch');
    if (isLowEnd.value) html.classList.add('is-low-end');
    html.classList.add(`os-${os.value}`);

    // Reduce animations on low-end devices
    if (isLowEnd.value) {
      html.classList.add('reduce-motion');
    }

    // Set CSS custom properties
    html.style.setProperty('--dpr', String(dpr.value));
    html.style.setProperty('--screen-width', `${width.value}px`);
    html.style.setProperty('--screen-height', `${height.value}px`);

    console.log('[Mobile] Device optimizations applied:', {
      type: isMobile.value ? 'mobile' : isTablet.value ? 'tablet' : 'desktop',
      lowEnd: isLowEnd.value,
      dpr: dpr.value
    });
  }

  /**
   * Optimize touch interactions
   */
  function optimizeTouchInteractions() {
    if (!isTouch.value || typeof document === 'undefined') return;

    // Add touch-action CSS for better scrolling
    document.body.style.touchAction = 'manipulation';

    // Prevent 300ms click delay on mobile
    const style = document.createElement('style');
    style.textContent = `
      * {
        touch-action: manipulation;
      }
      a, button, [role="button"] {
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
      }
    `;
    document.head.appendChild(style);

    console.log('[Mobile] Touch interactions optimized');
  }

  /**
   * Handle viewport resize
   */
  function handleResize() {
    width.value = window.innerWidth;
    height.value = window.innerHeight;
    dpr.value = window.devicePixelRatio || 1;

    // Update store
    store.updateDevice(deviceInfo.value);

    // Re-apply optimizations
    applyPerformanceOptimizations();
  }

  /**
   * Handle orientation change
   */
  function handleOrientationChange() {
    console.log('[Mobile] Orientation changed');
    handleResize();
  }

  // Lifecycle hooks
  onMounted(() => {
    if (typeof window === 'undefined') return;

    // Initial setup
    applyPerformanceOptimizations();
    optimizeTouchInteractions();

    // Update store
    store.updateDevice(deviceInfo.value);

    // Listen for resize and orientation changes
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleOrientationChange);

    console.log('✓ Device detection initialized:', deviceInfo.value);
  });

  onUnmounted(() => {
    if (typeof window === 'undefined') return;

    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleOrientationChange);
  });

  return {
    // Reactive device properties
    width,
    height,
    dpr,
    isMobile,
    isTablet,
    isDesktop,
    isTouch,
    os,
    isLowEnd,
    deviceInfo,

    // Methods
    getGlobeConfig,
    applyPerformanceOptimizations,
    optimizeTouchInteractions
  };
}
